// /projects/ss-time/frontend/app/src/app/core/services/auth.api.service.ts

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LoginWithPasswordPayload } from "../../shared/models/auth/login-with-password-payload";
import { ReLoginWithTokenPayload } from "../../shared/models/auth/re-login-with-token-payload";
import { UserModel } from "../../shared/models/user.model";
import { SignUpWithPasswordPayload } from '../../shared/models/auth/signup-payload';

export type MessageResponse = { message: string; [key: string]: any };
export type NonjointInviteLookupResponse = { loanId: string; applicantEmail: string | null; [key: string]: any };

@Injectable({
    providedIn: 'root'
})
export class AuthApiService {

    constructor(private http: HttpClient) {
    }

    /** Login response: either user (success) or requiresOtp (admin must enter OTP from their email). */
    authenticateWithPassword(payload: LoginWithPasswordPayload): Observable<UserModel | { requiresOtp: true; email: string }> {
        return this.http.post<UserModel | { requiresOtp: true; email: string }>(`/authentication/login`, payload.getPayload())
            .pipe(
                catchError(this.handleError)
            );
    }

    requestAdminResetOtp(token: string, colleagueEmail: string): Observable<{ message: string; email: string }> {
        return this.http.post<{ message: string; email: string }>(`/authentication/request-admin-reset-otp`, {
            token,
            colleagueEmail: colleagueEmail.trim().toLowerCase(),
        }).pipe(
            catchError(this.handleError)
        );
    }

    verifyAdminOtp(email: string, otp: string): Observable<UserModel> {
        return this.http.post<UserModel>(`/authentication/verify-admin-otp`, { email: email.trim().toLowerCase(), otp: otp.replace(/\s/g, '') })
            .pipe(
                catchError(this.handleError)
            );
    }

    authenticateWithToken(payload: ReLoginWithTokenPayload): Observable<UserModel> {
        return this.http.post<UserModel>(`/authentication/token-login`, payload.getPayload())
            .pipe(
                catchError(this.handleError)
            );
    }

    nonjointSignup(payload: SignUpWithPasswordPayload, loanId: string): Observable<MessageResponse> {
        // Backend now returns 201 with a message (requires email verification), not a session.
        return this.http.post<MessageResponse>(`/authentication/nonjoint-signup/${loanId}`, payload.getPayload())
            .pipe(
                catchError(this.handleError)
            );
    }

    signup(payload: SignUpWithPasswordPayload): Observable<MessageResponse> {
        // Backend now returns 201 with a message (requires email verification), not a session.
        return this.http.post<MessageResponse>(`/authentication/signup`, payload.getPayload())
            .pipe(
                catchError(this.handleError)
            );
    }

    forgotPassword(email: string): Observable<MessageResponse> {
        return this.http.post<MessageResponse>(`/authentication/forgot-password`, { email }).pipe(
            catchError(this.handleError)
        );
    }

    resetPassword(token: string, newPassword: string, adminOtp?: string): Observable<MessageResponse | { requiresAdminOtp: true; email: string }> {
        const body: { token: string; newPassword: string; adminOtp?: string } = { token, newPassword };
        if (adminOtp) body.adminOtp = adminOtp;
        return this.http.post<MessageResponse | { requiresAdminOtp: true; email: string }>(`/authentication/reset-password`, body).pipe(
            catchError(this.handleError)
        );
    }

    verifyEmail(token: string): Observable<MessageResponse> {
        return this.http.post<MessageResponse>(`/authentication/verify-email`, { token }).pipe(
            catchError(this.handleError)
        );
    }

    resendVerification(email: string): Observable<MessageResponse> {
        return this.http.post<MessageResponse>(`/authentication/resend-verification`, { email }).pipe(
            catchError(this.handleError)
        );
    }

    lookupNonjointInvite(token: string): Observable<NonjointInviteLookupResponse> {
        return this.http.post<NonjointInviteLookupResponse>(`/authentication/nonjoint-invite/lookup`, { token }).pipe(
            catchError(this.handleError)
        );
    }

    private handleError(error: any): Observable<never> {
        console.error('An error occurred', error); // Log error to console
        return throwError(() => error); // Rethrow the error
    }
}
